import { useEffect, useRef, useState } from 'react';
import { barcodeDetector } from './camera-support';

const FORMATS = ['code_128', 'code_39', 'qr_code', 'data_matrix', 'ean_13'];
const REPEAT_MS = 2500;

/**
 * Live camera scanning with the browser's native BarcodeDetector. The
 * camera is released when the scanner closes or unmounts.
 */
export function CameraScanner({
  onDetected,
  onClose,
}: {
  onDetected: (code: string) => void;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const detected = useRef(onDetected);
  const [error, setError] = useState<string | null>(null);
  detected.current = onDetected;

  useEffect(() => {
    const Detector = barcodeDetector();
    if (!Detector) {
      setError('This browser cannot scan with the camera. Use a scanner or type the code.');
      return;
    }
    const element = video.current;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;
    const lastSeen = new Map<string, number>();
    const detector = new Detector({ formats: FORMATS });

    async function start(): Promise<void> {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch {
        setError('The camera could not be opened. Check the browser permission.');
        return;
      }
      if (stopped || !element) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      element.srcObject = stream;
      await element.play().catch(() => undefined);
      const tick = async () => {
        if (stopped) return;
        try {
          const codes = await detector.detect(element);
          const now = Date.now();
          for (const { rawValue } of codes) {
            const value = rawValue.trim();
            if (!value || now - (lastSeen.get(value) ?? 0) < REPEAT_MS) continue;
            lastSeen.set(value, now);
            detected.current(value);
          }
        } catch {
          // A frame that can't be read is skipped.
        }
        timer = window.setTimeout(() => void tick(), 250);
      };
      void tick();
    }
    void start();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
      if (element) element.srcObject = null;
    };
  }, []);

  return (
    <div className="camera-scanner" role="group" aria-label="Camera scanner">
      {error ? (
        <p role="alert" className="form-error">
          {error}
        </p>
      ) : (
        <video
          ref={video}
          className="camera-preview"
          muted
          playsInline
          aria-label="Camera preview"
        />
      )}
      <button type="button" onClick={onClose}>
        Close camera
      </button>
    </div>
  );
}
